import { useState, useRef, useEffect } from 'react'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Search, X, Calendar, Building2, Send, FileText, Tag, FileBarChart, Filter, Megaphone, Check, ChevronDown } from 'lucide-react'
import type { CampaniaFilters, FiltersData } from '@/modules/multichannel/types/campania'

// Generar opciones de meses (últimos 12 meses + próximos 3)
function generateMonthOptions() {
  const months = []
  const now = new Date()

  // Últimos 12 meses
  for (let i = 12; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleDateString('es-EC', { year: 'numeric', month: 'long' }),
      year: date.getFullYear(),
      month: date.getMonth(),
    })
  }

  // Próximos 3 meses
  for (let i = 1; i <= 3; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1)
    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleDateString('es-EC', { year: 'numeric', month: 'long' }),
      year: date.getFullYear(),
      month: date.getMonth(),
    })
  }

  return months
}

function formatDateLocal(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getMonthRange(yearMonth: string) {
  const [year, month] = yearMonth.split('-').map(Number)
  const firstDay = new Date(year, month - 1, 1)
  const lastDay = new Date(year, month, 0)

  return {
    fecha_desde: formatDateLocal(firstDay),
    fecha_hasta: formatDateLocal(lastDay),
  }
}

// ¿El rango actual corresponde a meses completos (día 1 → último día del mes)?
// Si no, se muestra en modo "Rango personalizado".
function esRangoDeMesesCompletos(desde?: string, hasta?: string): boolean {
  if (!desde || !hasta) return true // sin fecha o mes actual por defecto
  const [dy, dm, dd] = desde.split('-').map(Number)
  const [hy, hm, hd] = hasta.split('-').map(Number)
  if (dd !== 1) return false
  const ultimoDiaHasta = new Date(hy, hm, 0).getDate()
  return hd === ultimoDiaHasta && (dy < hy || (dy === hy && dm <= hm))
}

interface CampaniasFiltersProps {
  filters: CampaniaFilters
  filtersData: FiltersData | undefined
  onFilterChange: (filters: CampaniaFilters) => void
  onClearFilters: () => void
  /** ID de marca principal forzado por el área seleccionada (bloquea el select) */
  areaLabMarcaId?: number
}

function MultiSelectMonth({
  options,
  selected,
  onChange,
}: {
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (selected: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const displayLabel = () => {
    if (selected.length === 0) return 'Todos los meses'
    if (selected.length === 1) return options.find((o) => o.value === selected[0])?.label ?? selected[0]
    return `${selected.length} meses`
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
      >
        <span className="truncate text-left">{displayLabel()}</span>
        <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[200px] rounded-md border bg-popover p-1 shadow-md">
          <div className="max-h-[240px] overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => toggle(option.value)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
              >
                <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${selected.includes(option.value) ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30'}`}>
                  {selected.includes(option.value) && <Check className="h-3 w-3" />}
                </div>
                <span className="truncate capitalize">{option.label}</span>
              </button>
            ))}
          </div>
          {selected.length > 0 && (
            <div className="border-t pt-1 mt-1 px-1">
              <button
                type="button"
                onClick={() => onChange([])}
                className="flex w-full items-center justify-center rounded-sm px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                Limpiar selección
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function MultiSelectCampania({
  options,
  selected,
  onChange,
}: {
  options: string[]
  selected: string[]
  onChange: (selected: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(search.toLowerCase())
  )

  const toggle = (name: string) => {
    if (selected.includes(name)) {
      onChange(selected.filter((s) => s !== name))
    } else {
      onChange([...selected, name])
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="truncate text-left">
          {selected.length === 0
            ? 'Todas'
            : selected.length === 1
              ? selected[0]
              : `${selected.length} seleccionadas`}
        </span>
        <svg className="h-4 w-4 opacity-50 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[220px] rounded-md border bg-popover p-1 shadow-md">
          <div className="px-1 pb-1">
            <Input
              placeholder="Buscar campaña..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-2 px-2 text-sm text-muted-foreground">Sin resultados</div>
            ) : (
              filtered.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggle(name)}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                >
                  <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${selected.includes(name) ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30'}`}>
                    {selected.includes(name) && <Check className="h-3 w-3" />}
                  </div>
                  <span className="truncate">{name}</span>
                </button>
              ))
            )}
          </div>
          {selected.length > 0 && (
            <div className="border-t pt-1 mt-1 px-1">
              <button
                type="button"
                onClick={() => onChange([])}
                className="flex w-full items-center justify-center rounded-sm px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                Limpiar selección
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function CampaniasFilters({
  filters,
  filtersData,
  onFilterChange,
  onClearFilters,
  areaLabMarcaId,
}: CampaniasFiltersProps) {
  const handleSearchChange = (search: string) => {
    onFilterChange({ ...filters, search })
  }

  const handleSelectChange = (key: keyof CampaniaFilters, value: string) => {
    onFilterChange({
      ...filters,
      [key]: value === 'all' ? undefined : value,
    })
  }

  const handleMonthsChange = (months: string[]) => {
    if (months.length === 0) {
      const newFilters = { ...filters }
      delete newFilters.fecha_desde
      delete newFilters.fecha_hasta
      onFilterChange(newFilters)
    } else {
      const sorted = [...months].sort()
      const { fecha_desde } = getMonthRange(sorted[0])
      const { fecha_hasta } = getMonthRange(sorted[sorted.length - 1])
      onFilterChange({ ...filters, fecha_desde, fecha_hasta })
    }
  }

  // Modo del filtro de fecha: por meses completos o rango personalizado. Se
  // inicializa según el rango vigente para no descuadrar si llega uno arbitrario.
  const [modoFecha, setModoFecha] = useState<'mes' | 'rango'>(
    esRangoDeMesesCompletos(filters.fecha_desde, filters.fecha_hasta) ? 'mes' : 'rango',
  )

  const handleRangeChange = (campo: 'fecha_desde' | 'fecha_hasta', value: string) => {
    const next = { ...filters }
    if (!value) delete next[campo]
    else next[campo] = value
    onFilterChange(next)
  }

  const handleNombresCampaniaChange = (nombres: string[]) => {
    onFilterChange({
      ...filters,
      nombres_campania: nombres.length > 0 ? nombres : undefined,
    })
  }

  // Derivar meses seleccionados del rango fecha_desde / fecha_hasta
  const getSelectedMonths = (): string[] => {
    if (!filters.fecha_desde || !filters.fecha_hasta) return []
    const [fy, fm] = filters.fecha_desde.split('-').map(Number)
    const [ty, tm] = filters.fecha_hasta.split('-').map(Number)
    const months: string[] = []
    let y = fy, m = fm
    while (y < ty || (y === ty && m <= tm)) {
      months.push(`${y}-${String(m).padStart(2, '0')}`)
      m++
      if (m > 12) { m = 1; y++ }
    }
    return months
  }

  const monthOptions = generateMonthOptions()

  const hasActiveFilters = Object.values(filters).some((value) => value !== undefined)

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700"
      style={{ background: 'linear-gradient(to right, #8d82bc08, transparent 40%)' }}
    >
      <div className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-full p-1.5" style={{ backgroundColor: '#8d82bc15' }}>
              <Filter className="h-4 w-4" style={{ color: '#8d82bc' }} />
            </div>
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Filtros</span>
            {filters.nombres_campania && filters.nombres_campania.length > 0 && (
              <div className="flex items-center gap-1 ml-2">
                {filters.nombres_campania.map((name) => (
                  <Badge key={name} variant="secondary" className="text-[10px] px-1.5 py-0 gap-1">
                    {name}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-foreground"
                      onClick={() => handleNombresCampaniaChange(filters.nombres_campania!.filter((n) => n !== name))}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={onClearFilters} className="text-xs text-muted-foreground hover:text-foreground">
              <X className="mr-1.5 h-3.5 w-3.5" />
              Limpiar
            </Button>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {/* Fecha: por mes completo o rango personalizado */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                {modoFecha === 'mes' ? 'Mes' : 'Rango'}
              </label>
              <div className="flex items-center rounded-md border border-input p-0.5">
                {(['mes', 'rango'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setModoFecha(m)}
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium capitalize transition-colors ${
                      modoFecha === m
                        ? 'text-white'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    style={modoFecha === m ? { backgroundColor: '#8d82bc' } : undefined}
                  >
                    {m === 'mes' ? 'Mes' : 'Rango'}
                  </button>
                ))}
              </div>
            </div>
            {modoFecha === 'mes' ? (
              <MultiSelectMonth
                options={monthOptions}
                selected={getSelectedMonths()}
                onChange={handleMonthsChange}
              />
            ) : (
              <div className="flex items-center gap-1.5">
                <Input
                  type="date"
                  value={filters.fecha_desde || ''}
                  max={filters.fecha_hasta}
                  onChange={(e) => handleRangeChange('fecha_desde', e.target.value)}
                  className="h-9"
                  aria-label="Desde"
                />
                <span className="text-xs text-muted-foreground">→</span>
                <Input
                  type="date"
                  value={filters.fecha_hasta || ''}
                  min={filters.fecha_desde}
                  onChange={(e) => handleRangeChange('fecha_hasta', e.target.value)}
                  className="h-9"
                  aria-label="Hasta"
                />
              </div>
            )}
          </div>

          {/* Búsqueda */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Search className="h-3 w-3" />
              Búsqueda
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar campaña..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>

          {/* Laboratorio */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Building2 className="h-3 w-3" />
              Laboratorio
            </label>
            <Select
              value={areaLabMarcaId?.toString() || filters.marca_principal_id?.toString() || 'all'}
              onValueChange={(value) => handleSelectChange('marca_principal_id', value)}
              disabled={!!areaLabMarcaId}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {filtersData?.marcas_principales.map((marca) => (
                  <SelectItem key={marca.id} value={marca.id.toString()}>
                    {marca.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Campaña (multiselect con buscador) */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Megaphone className="h-3 w-3" />
              Campaña
            </label>
            <MultiSelectCampania
              options={filtersData?.nombres_campania || []}
              selected={filters.nombres_campania || []}
              onChange={handleNombresCampaniaChange}
            />
          </div>

          {/* Tipo Campaña */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Tag className="h-3 w-3" />
              Tipo
            </label>
            <Select
              value={filters.tipo || 'all'}
              onValueChange={(value) => handleSelectChange('tipo', value)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {filtersData?.tipos_campania.map((tipo) => (
                  <SelectItem key={tipo.id} value={tipo.nombre}>
                    {tipo.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estado Envío */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Send className="h-3 w-3" />
              Estado Envío
            </label>
            <Select
              value={filters.estado_envio || 'all'}
              onValueChange={(value) => handleSelectChange('estado_envio', value)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {filtersData?.estados_envio.map((estado) => (
                  <SelectItem key={estado.id} value={estado.nombre}>
                    {estado.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estado Contenido */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <FileText className="h-3 w-3" />
              Estado Contenido
            </label>
            <Select
              value={filters.estado_contenido || 'all'}
              onValueChange={(value) => handleSelectChange('estado_contenido', value)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {filtersData?.estados_contenido.map((estado) => (
                  <SelectItem key={estado.id} value={estado.nombre}>
                    {estado.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estado Reporte */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <FileBarChart className="h-3 w-3" />
              Estado Reporte
            </label>
            <Select
              value={filters.estado_reporte || 'all'}
              onValueChange={(value) => handleSelectChange('estado_reporte', value)}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {filtersData?.estados_reporte.map((estado) => (
                  <SelectItem key={estado.id} value={estado.nombre}>
                    {estado.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  )
}
